import React from "react";
import ProgressBar from "components/ProgressBar";
import moment from "moment";
import { userService } from "services/userService";
import { Col, Form, Row } from "react-bootstrap";
import { Formik } from "formik";

import {
  getPaymentRequirement,
  registerEmployer,
} from "actions/employerActions";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import type { ApolloClient } from "@apollo/client";
import {
  every,
  get,
  includes,
  intersectionBy,
  isEmpty,
  isNull,
  pick,
  replace,
  some,
  toLower,
  toUpper,
} from "lodash";
import { toast } from "react-toastify";

import {
  businessTypes,
  employeeRange,
  naicsCategories,
  naicsValues,
} from "statics/businessCodes";
import {
  INVALID_PHONE_NUMBER,
  noWhitespaceRegex,
  postalCodeValidator,
  testEIN,
  testPhoneNumber,
  urlValidator,
  validateUsersAddress,
  whiteSpaceError,
  EmployerAddress1Validator,
  EmployerAddress2Validator,
  PRINTABLE_ASCII_MESSAGE,
  testPrintableAsciiCharacters,
  ALPHANUMERIC_SPACES_MESSAGE,
  testAlphanumericSpaces,
  stateValidator,
} from "utils/fieldValidators";
import { employerConstants, userConstants } from "actions/types";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import Button from "components/Button";
import Alert from "components/Alert";
import queryString from "query-string";
import MaskedInput from "components/MaskedInput";
import StateSelect from "components/StateSelect";
import AddressValidator from "components/AddressValidator";
import IconSpinner from "components/IconSpinner";
import EmployerOwnerInfo from "./EmployerOwnerInfo";

import "./EmployerEnroll.scss";
import { getUserState } from "actions/userActions";
import {
  auth0PlanCodeSelector,
  auth0ReferralCodeSelector,
} from "store/selectors/auth0";
import { ENTERPRISE, findPlan } from "statics/planCodes";
import PlanCard from "./PlanCard";
import {
  showsOrderBillingStepSelector,
  planCodeSelector,
} from "store/selectors/employer";
import { COMPANY_INFO } from "statics/onboardingSteps";
import {
  alphabeticalPayrollProviders,
  isolvedPartnerReferralCodes,
  isolvedProviders,
  wholesaleBillingProviders,
  wholesaleBillingReferralCodes,
} from "statics/payrollProviders";
import { ScrollToFieldError } from "utils/form";
import { postEmployerTermsSubmittedStates } from "statics/states";
import AccountProcedureDisclosure from "components/AccountProcedureDisclosure";
import * as yup from "yup";
import type { RootState } from "types/state";
import type { GetValidatedAddressQuery } from "types/graphql";

type ValidatedAddress = GetValidatedAddressQuery["getValidatedAddress"];

interface FormValues {
  businessName: string;
  businessDBA: string;
  ein: string;
  confirmEin: string;
  businessType: string;
  naicsCodeCategory: string;
  naicsCode: string;
  businessSize: string | null;
  businessSizeDisplayValue: number | string;
  referralCode: string;
  businessURL: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  postalCode: string;
  phone: string;
  phoneExt: string;
  payrollProvider: string;
  registrationDate: string;
  needsStatePlan: string;
}

interface CompanyState {
  businessName: string;
  businessDBA: string;
  ein: string;
  businessType: string;
  naicsCodeCategory: string;
  naicsCode: string;
  businessSize: string | null;
  referralCode: string;
  businessURL: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  postalCode: string;
  phone: string;
  phoneExt: string;
  payrollProvider: string;
  registrationDate: string;
}

function testNaicsCode(value: string | undefined) {
  const regex = /99999/;
  const match = regex.test(value as string);
  if (match) {
    return false;
  } else {
    return true;
  }
}

const schema = yup.object({
  businessName: yup
    .string()
    .label("Company Legal Name")
    .required()
    .min(3, "Must be at least three characters.")
    .max(200, "Must be less than 200 characters.")
    .matches(noWhitespaceRegex, whiteSpaceError),
  businessDBA: yup
    .string()
    .label("Company DBA")
    .min(1, "Must be at least one character.")
    .max(200),
  ein: yup
    .string()
    .label("EIN")
    .required()
    .max(10)
    .test("ein", "${path} is not a valid format. Ex: 12-3456789", testEIN),
  confirmEin: yup
    .string()
    .label("Confirm EIN")
    .required()
    .max(10)
    .test("eins-match", `EIN must match`, function (value) {
      return this.parent.ein === value;
    }),
  businessType: yup.string().label("Incorporation Type").required(),
  naicsCode: yup.string().label("Industry Type").required(),
  businessSize: yup.string().label("Company size").required(),
  businessSizeDisplayValue: yup
    .number()
    .label("Company size")
    .required()
    .min(1),
  businessURL: yup
    .string()
    .label("Website URL")
    .max(200)
    .test("businessURL", "Enter a valid website URL", urlValidator),
  naicsCodeCategory: yup
    .string()
    .label("Industry Category")
    .required()
    .test("naicsCat", "Please choose an Industry Category", testNaicsCode),
  address1: EmployerAddress1Validator,
  address2: EmployerAddress2Validator,
  city: yup
    .string()
    .label("City")
    .required()
    .test("city", PRINTABLE_ASCII_MESSAGE, testPrintableAsciiCharacters)
    .test("city", ALPHANUMERIC_SPACES_MESSAGE, testAlphanumericSpaces)
    .max(40)
    .matches(noWhitespaceRegex, whiteSpaceError),
  state: stateValidator,
  postalCode: postalCodeValidator,
  phone: yup
    .string()
    .trim()
    .label("Phone")
    .required()
    .test("phone", PRINTABLE_ASCII_MESSAGE, testPrintableAsciiCharacters)
    .test("us-phone", INVALID_PHONE_NUMBER, testPhoneNumber)
    .max(15),
  phoneExt: yup.string().label("Phone Ext").max(10),
  referralCode: yup.string().label("Referral Code").max(30),
  needsStatePlan: yup.string().label("Satisfy State Mandate").required(),
  payrollProvider: yup.string().label("Payroll Provider").required(),
  registrationDate: yup
    .string()
    .test(
      "is-date",
      "Please enter a valid date in MM/DD/YYYY format",
      (value) => {
        if (!value) return false;
        const date = moment(value, "MM/DD/YYYY", true);
        return date.isValid();
      }
    )
    .label("Date of Incorporation")
    .required()
    // PRESERVED BUG (conversion-only): .max() on a yup string schema expects a
    // numeric max length, but a Date is passed (coerced at runtime) — kept verbatim.
    // @ts-expect-error string().max expects a number, not a Date
    .max(new Date(), "Date of Incorporation cannot be in the future"),
});

const verifyCompany = (company: Record<string, unknown>) => {
  const userCompany = pick(company, [
    "id",
    "name",
    "ein",
    "type",
    "naicsCode",
    "size",
    "address.address1",
    "address.city",
    "address.state",
    "address.postalCode",
    "address.phone",
  ]);
  return every(userCompany, (val) => !isNull(val) && !isEmpty(val));
};

const actions = [employerConstants.REGISTER_EMPLOYER, userConstants.USER_STATE];
const loadingSelector = createLoadingSelector(actions);
const errorSelector = createErrorSelector(actions);

const mapStateToProps = (state: RootState, ownProps: object) => {
  const {
    employer: { company },
  } = state;
  return {
    company,
    ownProps,
    isMultiAccount: state.employer.isMultiAccount,
    organizationId: get(state.employer, "organization.id"),
    error: errorSelector(state),
    isFetching: loadingSelector(state),
    companyState: get(state.user, "companyState.state"),
    initialPlanCode: auth0PlanCodeSelector(state),
    savedPlanCode: planCodeSelector(state),
    referralCode: auth0ReferralCodeSelector(state),
    showsOrderBillingStep: showsOrderBillingStepSelector(state),
  };
};

const mapDispatchToProps = {
  registerEmployer,
  getUserState,
  getPaymentRequirement,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type Props = WithApolloClient<ConnectedProps<typeof connector>>;

interface State {
  company: CompanyState;
  disableIndustryType: boolean;
  submitted: boolean;
  loading: boolean;
  showAddressModal: boolean;
  fetchingAddress: boolean;
  urlHadInviteCode: boolean;
  showOwnerInfoPage: boolean;
  validatedAddress?: ValidatedAddress;
}

class EmployerEnroll extends React.Component<Props, State> {
  _isMounted = false;

  constructor(props: Props) {
    super(props);

    const referralCode =
      (queryString.parse(window.location.search).referralCode as
        | string
        | null) || "";

    this.state = {
      company: {
        businessName: "",
        businessDBA: "",
        ein: "",
        businessType: "",
        naicsCodeCategory: "",
        naicsCode: "",
        businessSize: null,
        referralCode,
        businessURL: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        postalCode: "",
        phone: "",
        phoneExt: "",
        payrollProvider: "",
        registrationDate: "",
      },
      disableIndustryType: true,
      submitted: false,
      loading: true,
      showAddressModal: false,
      fetchingAddress: false,
      urlHadInviteCode: !!referralCode,
      showOwnerInfoPage: false,
    };
  }

  async componentDidMount() {
    window.analytics.page("Employer Company Info");
    this._isMounted = true;

    const isAddingEmp =
      this.props.isMultiAccount &&
      includes(postEmployerTermsSubmittedStates, this.props.companyState);

    const identityIsVerified =
      !isAddingEmp &&
      !isEmpty(this.props.company) &&
      verifyCompany(this.props.company);

    if (identityIsVerified) {
      void this.props.getPaymentRequirement(
        this.props.client as ApolloClient<object>
      );
    }
    this.setState({
      submitted: false,
      loading: false,
      showOwnerInfoPage: identityIsVerified,
    });
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  submitEmployer = async (
    values: FormValues,
    skipAddressValidation = false
  ) => {
    if (!skipAddressValidation) {
      try {
        this.setState({ fetchingAddress: true });

        const companyAddress = {
          address1: values.address1,
          address2: values.address2,
          state: values.state,
          postalCode: values.postalCode,
          city: values.city,
        };

        const validatedAddress = await userService.getValidatedAddress(
          this.props.client as ApolloClient<object>,
          companyAddress
        );

        this.setState({ fetchingAddress: false });

        const userAddressIsValid = validateUsersAddress(
          companyAddress,
          // validatedAddress has string|null fields (and may be null) from the
          // GraphQL query vs AddressFields' string|undefined; passed verbatim.
          validatedAddress as unknown as Record<string, string | undefined>
        );

        if (!userAddressIsValid) {
          this.setState({
            showAddressModal: true,
            validatedAddress,
          });

          return;
        }
      } catch (error) {
        // if the validation endpoint fails we dont want to prevent user from saving so continue on and log error
        console.log(error);
      }
    }

    let company = {
      name: values.businessName,
      dba: values.businessDBA,
      ein: values.ein,
      type: values.businessType,
      naicsCode: values.naicsCode,
      size: values.businessSize,
      referralCode: values.referralCode || "",
      url: values.businessURL,
      address1: values.address1,
      address2: values.address2,
      city: values.city,
      state: values.state,
      postalCode: values.postalCode,
      phone: values.phone,
      phoneExt: values.phoneExt,
      payrollProvider: values.payrollProvider,
      planCode: this._getPlanCode(values),
      registrationDate: values.registrationDate,
      // only present when an existing login follows a signup link, new logins have it saved at signup
      signupCode:
        toUpper(
          (queryString.parse(window.location.search).signupCode as
            | string
            | null) || ""
        ) || null,
    };
    const registered = await this.props.registerEmployer(
      this.props.client as ApolloClient<object>,
      {
        // size is FormValues.businessSize (string | null); the register mutation
        // types it as a required string. Passed verbatim, narrowed at the boundary.
        company: company as typeof company & { size: string },
        organizationId: this.props.organizationId,
        needsStatePlan: values.needsStatePlan === "yes",
      }
    );

    if (registered) {
      await this.props.getUserState(this.props.client as ApolloClient<object>);
      toast.success("Successfully registered company.");
      void this.props.getPaymentRequirement(
        this.props.client as ApolloClient<object>
      );

      this.setState({
        showOwnerInfoPage: true,
      });
    }

    window.analytics.track("Submitted Company Information", {});

    if (this._isMounted) {
      this.setState({
        submitted: true,
      });
    }
  };

  _closeModal = () => {
    this.setState({ showAddressModal: false });
  };

  _getPlanCode = (values: FormValues): string | null => {
    if (!this.props.initialPlanCode) return null;

    const isWholesaleBillingReferral = some(
      wholesaleBillingReferralCodes,
      (code) => {
        return toLower(code) === toLower(this.state.company.referralCode);
      }
    );

    const isWholesaleBillingProvider = includes(
      wholesaleBillingProviders,
      values.payrollProvider
    );
    if (isWholesaleBillingProvider || isWholesaleBillingReferral) {
      return null;
    }

    const isSelfServePlanCode = includes(this.props.initialPlanCode, "WSPLN");

    if (!isSelfServePlanCode) {
      return this.props.initialPlanCode;
    }

    if (this.props.initialPlanCode && !values.businessSizeDisplayValue) {
      return this.props.initialPlanCode;
    }

    const matchingPlan = findPlan(
      values.businessSizeDisplayValue as number,
      this.props.initialPlanCode
    );

    if (matchingPlan && this.props.initialPlanCode) return matchingPlan.id;

    return null;
  };

  _getPayrollProvidersToUse = (): typeof alphabeticalPayrollProviders => {
    const isIsolvedReferral = some(
      isolvedPartnerReferralCodes,
      (code) => toLower(code) === toLower(this.props.referralCode)
    );

    // only show isolved payroll providers if they came in with isolved referral code
    if (isIsolvedReferral) {
      return intersectionBy(
        alphabeticalPayrollProviders,
        // isolvedProviders is a string[]; the iteratee reads .id off provider
        // objects and falls back to the raw string for the string[] side.
        isolvedProviders as unknown as typeof alphabeticalPayrollProviders,
        (val) => {
          return val.id || val;
        }
      );
    }

    return alphabeticalPayrollProviders;
  };

  renderEmployerForm = () => {
    const { company } = this.state;

    var articleStyle = {
      paddingBottom: 0,
    };

    const payrollProvidersToUse = this._getPayrollProvidersToUse();

    return (
      <div>
        <Formik<FormValues>
          validateOnChange={false}
          validationSchema={schema}
          onSubmit={(values) => {
            void this.submitEmployer(values);
          }}
          enableReinitialize={true}
          initialValues={{
            ...company,
            needsStatePlan: "",
            businessSizeDisplayValue: "",
            confirmEin: "",
            ein: "",
          }}
        >
          {({
            handleSubmit,
            handleChange,
            handleBlur,
            setFieldValue,
            setFieldTouched,
            values,
            touched,
            errors,
          }) => (
            <Form noValidate onSubmit={handleSubmit}>
              <ScrollToFieldError />
              {this.state.showAddressModal && (
                <AddressValidator
                  show={this.state.showAddressModal}
                  // @ts-expect-error AddressValidator invokes onSubmit with its
                  // own AddressValidatorAddress shape; submitEmployer narrows to
                  // FormValues at runtime — structurally compatible address fields.
                  onSubmit={this.submitEmployer}
                  setFieldValue={setFieldValue}
                  // @ts-expect-error validatedAddress has string|null fields from
                  // the GraphQL query vs the prop's string|undefined; inert.
                  validatedAddress={this.state.validatedAddress}
                  onClose={this._closeModal}
                  // @ts-expect-error Formik FormValues is a superset of the
                  // AddressValidatorAddress fields the modal actually reads.
                  values={values}
                  allowPoBox={true}
                />
              )}
              <div className="employer-enroll-layout">
                <div className="mega-container">
                  <div
                    className="step-container is-active"
                    data-circle-percent="30"
                  >
                    <section className="page-title-wrap">
                      <article className="text-cell">
                        <p className="page-title">Company Verification</p>
                      </article>
                    </section>
                    <div id="form-employer-company-profile">
                      <section className="form-sec-2col">
                        <article className="col-form" style={articleStyle}>
                          <Row className="mb-3">
                            <Form.Group
                              as={Col}
                              md={10}
                              controlId="formBasicBusinessName"
                            >
                              <p className="page-label">Company Information</p>
                              <p className="page-subtext">
                                We are required by law to identify and verify
                                your business to create your Icon account.
                              </p>
                              <Form.Control
                                name="businessName"
                                placeholder="Company Legal Name"
                                value={values.businessName}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isInvalid={
                                  touched.businessName && !!errors.businessName
                                }
                                isValid={
                                  touched.businessName && !errors.businessName
                                }
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.businessName}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Row>

                          <Row className="mb-3">
                            <Form.Group
                              as={Col}
                              md={10}
                              controlId="formBasicBusinessDBA"
                            >
                              <Form.Control
                                name="businessDBA"
                                placeholder="Company DBA"
                                value={values.businessDBA}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isInvalid={
                                  touched.businessDBA && !!errors.businessDBA
                                }
                                isValid={
                                  touched.businessDBA && !errors.businessDBA
                                }
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.businessDBA}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Row>

                          <Row className="mb-3">
                            <Form.Group
                              as={Col}
                              sm={4}
                              controlId="formBasicBusinessSize"
                            >
                              <Form.Control
                                name="businessSizeDisplayValue"
                                placeholder="Number of employees"
                                value={values.businessSizeDisplayValue}
                                onBlur={handleBlur}
                                onChange={(e) => {
                                  const rawValueFromInputString = +replace(
                                    e.target.value,
                                    /\D/g,
                                    ""
                                  );
                                  void setFieldValue(
                                    "businessSizeDisplayValue",
                                    rawValueFromInputString
                                  );
                                  void setFieldValue(
                                    "businessSize",
                                    employeeRange(rawValueFromInputString)
                                  );
                                }}
                                isInvalid={
                                  touched.businessSizeDisplayValue &&
                                  !!errors.businessSizeDisplayValue
                                }
                                isValid={
                                  touched.businessSizeDisplayValue &&
                                  !errors.businessSizeDisplayValue
                                }
                              />

                              <Form.Control.Feedback type="invalid">
                                {errors.businessSizeDisplayValue}
                              </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group
                              as={Col}
                              sm={6}
                              controlId="formBasicBusinessType"
                            >
                              <Form.Select
                                // @ts-expect-error Form.Select ignores the sm prop (inert layout leftover)
                                sm={4}
                                name="businessType"
                                value={values.businessType}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isInvalid={
                                  touched.businessType && !!errors.businessType
                                }
                                isValid={
                                  touched.businessType && !errors.businessType
                                }
                              >
                                <option value="" disabled>
                                  Incorporation Type
                                </option>
                                {businessTypes.map((item) => (
                                  <option value={item.value} key={item.value}>
                                    {item.description}
                                  </option>
                                ))}
                              </Form.Select>
                              <Form.Control.Feedback type="invalid">
                                {errors.businessType}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Row>

                          <Row className="mb-3" style={{ marginBottom: 12 }}>
                            <Form.Group
                              as={Col}
                              sm={4}
                              controlId="formBasicBusinessEIN"
                            >
                              <Form.Control
                                as={MaskedInput}
                                mask="00-0000000"
                                name="ein"
                                placeholder="EIN"
                                value={values.ein}
                                onAccept={(val: unknown, mask: unknown) =>
                                  handleChange({
                                    target: {
                                      name: (
                                        mask as {
                                          el: { input: HTMLInputElement };
                                        }
                                      ).el.input.name,
                                      value: val,
                                    },
                                  } as unknown as React.ChangeEvent<HTMLInputElement>)
                                }
                                onBlur={handleBlur}
                                isInvalid={touched.ein && !!errors.ein}
                                isValid={touched.ein && !errors.ein}
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.ein}
                              </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group
                              as={Col}
                              sm={4}
                              controlId="formBusinessConfirmEIN"
                            >
                              <Form.Control
                                as={MaskedInput}
                                mask="00-0000000"
                                name="confirmEin"
                                placeholder="Confirm EIN"
                                value={values.confirmEin}
                                onAccept={(val: unknown, mask: unknown) =>
                                  handleChange({
                                    target: {
                                      name: (
                                        mask as {
                                          el: { input: HTMLInputElement };
                                        }
                                      ).el.input.name,
                                      value: val,
                                    },
                                  } as unknown as React.ChangeEvent<HTMLInputElement>)
                                }
                                onBlur={handleBlur}
                                isInvalid={
                                  touched.confirmEin && !!errors.confirmEin
                                }
                                isValid={
                                  touched.confirmEin && !errors.confirmEin
                                }
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.confirmEin}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Row>
                          <Row className="mb-3">
                            <Form.Group
                              as={Col}
                              md={4}
                              controlId="formBasicDateOfIncorporation"
                            >
                              <Form.Control
                                as={MaskedInput}
                                mask="00/00/0000"
                                name="registrationDate"
                                placeholder="Date of Incorporation"
                                value={values.registrationDate}
                                onAccept={(val: unknown, mask: unknown) =>
                                  handleChange({
                                    target: {
                                      name: (
                                        mask as {
                                          el: { input: HTMLInputElement };
                                        }
                                      ).el.input.name,
                                      value: val,
                                    },
                                  } as unknown as React.ChangeEvent<HTMLInputElement>)
                                }
                                onBlur={handleBlur}
                                isInvalid={
                                  touched.registrationDate &&
                                  !!errors.registrationDate
                                }
                                isValid={
                                  touched.registrationDate &&
                                  !errors.registrationDate
                                }
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.registrationDate}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Row>
                        </article>
                      </section>

                      <section className="form-sec-2col">
                        <article className="col-form">
                          <p className="page-label">Company Setup</p>
                          <Row className="mb-3">
                            <Form.Group
                              as={Col}
                              sm={6}
                              controlId="formBasicPayrollProvider"
                            >
                              <Form.Label>
                                What payroll service do you use?
                              </Form.Label>
                              <Form.Select
                                // @ts-expect-error Form.Select ignores the sm prop (inert layout leftover)
                                sm={6}
                                name="payrollProvider"
                                value={values.payrollProvider}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isInvalid={
                                  touched.payrollProvider &&
                                  !!errors.payrollProvider
                                }
                                isValid={
                                  touched.payrollProvider &&
                                  !errors.payrollProvider
                                }
                              >
                                <option value="" disabled>
                                  Payroll Provider
                                </option>
                                {payrollProvidersToUse.map((item) => (
                                  <option value={item.id} key={item.id}>
                                    {item.label}
                                  </option>
                                ))}
                              </Form.Select>

                              <Form.Control.Feedback type="invalid">
                                {errors.payrollProvider}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Row>
                          <Row className="mb-3">
                            <Form.Group
                              as={Col}
                              controlId="formBasicNeedStatePlan"
                            >
                              <Form.Label>
                                Are you going to be using Icon to satisfy a
                                state mandate (e.g. CalSavers)?
                              </Form.Label>

                              <Form.Select
                                // @ts-expect-error Form.Select ignores the sm prop (inert layout leftover)
                                sm={2}
                                name="needsStatePlan"
                                value={values.needsStatePlan}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isInvalid={
                                  touched.needsStatePlan &&
                                  !!errors.needsStatePlan
                                }
                                isValid={
                                  touched.needsStatePlan &&
                                  !errors.needsStatePlan
                                }
                                style={{ width: 140 }}
                              >
                                <option
                                  value={""}
                                  key={"blank-state-label"}
                                  disabled
                                >
                                  Select an option
                                </option>
                                <option value={"yes"} key={"yes"}>
                                  Yes
                                </option>
                                <option value={"no"} key={"no"}>
                                  No
                                </option>
                              </Form.Select>
                              <Form.Control.Feedback type="invalid">
                                {errors.needsStatePlan}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Row>
                        </article>
                      </section>
                      <section className="form-sec-2col">
                        <article className="col-form" style={articleStyle}>
                          <Row className="mb-3">
                            <Form.Group
                              as={Col}
                              md={10}
                              controlId="formBasicBusinessNAICSCategory"
                            >
                              <p className="page-label">Company Industry </p>
                              <p className="page-subtext">
                                Choosing a category and type helps us verify
                                your information. It doesn{`'`}t have to be an
                                exact match, just pick the best one you see.
                              </p>
                              <Form.Select
                                // @ts-expect-error Form.Select ignores the sm prop (inert layout leftover)
                                sm={6}
                                name="naicsCodeCategory"
                                value={values.naicsCodeCategory}
                                onChange={(e) => {
                                  handleChange(e);
                                  this.setState({
                                    disableIndustryType: false,
                                  });
                                  // reset the code values if the category changes
                                  void setFieldValue("naicsCode", "");
                                  void setFieldTouched(
                                    "naicsCode",
                                    false,
                                    false
                                  );
                                }}
                                onBlur={handleBlur}
                                isInvalid={
                                  touched.naicsCodeCategory &&
                                  !!errors.naicsCodeCategory
                                }
                                isValid={
                                  touched.naicsCodeCategory &&
                                  !errors.naicsCodeCategory
                                }
                              >
                                <option value="" disabled>
                                  Industry Category
                                </option>
                                {naicsCategories.map((item, index) => (
                                  <option value={item.value} key={index}>
                                    {item.value}
                                  </option>
                                ))}
                              </Form.Select>
                              <Form.Control.Feedback type="invalid">
                                {errors.naicsCodeCategory}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Row>
                          <Row className="mb-3">
                            <Form.Group
                              as={Col}
                              md={12}
                              controlId="formBasicBusinessNAICS"
                            >
                              <Form.Select
                                // @ts-expect-error Form.Select ignores the sm prop (inert layout leftover)
                                sm={6}
                                name="naicsCode"
                                value={values.naicsCode}
                                onChange={(e) => {
                                  void setFieldTouched(
                                    "naicsCode",
                                    false,
                                    false
                                  );
                                  handleChange(e);
                                }}
                                onBlur={handleBlur}
                                isInvalid={
                                  touched.naicsCode && !!errors.naicsCode
                                }
                                isValid={touched.naicsCode && !errors.naicsCode}
                                disabled={this.state.disableIndustryType}
                              >
                                <option value="" disabled>
                                  Industry Type
                                </option>
                                {naicsValues
                                  .filter(
                                    (i) =>
                                      i.category === values.naicsCodeCategory
                                  )
                                  .map((item, index) => (
                                    <option value={item.value} key={index}>
                                      {item.description}
                                    </option>
                                  ))}
                              </Form.Select>
                              <Form.Control.Feedback type="invalid">
                                {errors.naicsCode}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Row>
                        </article>
                      </section>

                      <section className="form-sec-2col">
                        <article className="col-form">
                          <Form.Group
                            className="mb-3"
                            controlId="formBasicAddress1"
                          >
                            <p className="page-label">Company Address</p>
                            <Form.Control
                              name="address1"
                              placeholder="Address 1"
                              value={values.address1}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              isInvalid={touched.address1 && !!errors.address1}
                              isValid={touched.address1 && !errors.address1}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.address1}
                            </Form.Control.Feedback>
                          </Form.Group>
                          <Form.Group
                            className="mb-3"
                            controlId="formBasicAddress2"
                          >
                            <Form.Control
                              name="address2"
                              placeholder="Address 2"
                              value={values.address2}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              isInvalid={touched.address2 && !!errors.address2}
                              isValid={touched.address2 && !errors.address2}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.address2}
                            </Form.Control.Feedback>
                          </Form.Group>
                          <Row className="mb-3">
                            <Form.Group as={Col} controlId="formBasicCity">
                              <Form.Control
                                name="city"
                                placeholder="City"
                                value={values.city}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isInvalid={touched.city && !!errors.city}
                                isValid={touched.city && !errors.city}
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.city}
                              </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group
                              as={Col}
                              sm={4}
                              controlId="formBasicState"
                            >
                              <StateSelect
                                value={values.state}
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                                touched={touched}
                                errors={errors}
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.state}
                              </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group
                              as={Col}
                              sm={2}
                              controlId="formBasicPostalCode"
                            >
                              <Form.Control
                                name="postalCode"
                                placeholder="Zip"
                                value={values.postalCode}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isInvalid={
                                  touched.postalCode && !!errors.postalCode
                                }
                                isValid={
                                  touched.postalCode && !errors.postalCode
                                }
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.postalCode}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Row>
                          <Form.Group
                            className="mb-3"
                            controlId="formBasicBusinessURL"
                          >
                            <Form.Control
                              name="businessURL"
                              placeholder="Website"
                              value={values.businessURL}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              isInvalid={
                                touched.businessURL && !!errors.businessURL
                              }
                              isValid={
                                touched.businessURL && !errors.businessURL
                              }
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.businessURL}
                            </Form.Control.Feedback>
                          </Form.Group>
                          <Row className="mb-3">
                            <Form.Group
                              as={Col}
                              sm={4}
                              controlId="formBasicPhone"
                            >
                              <Form.Control
                                as={MaskedInput}
                                mask="000-000-0000"
                                name="phone"
                                placeholder="Phone"
                                value={values.phone}
                                onAccept={(val: unknown, mask: unknown) =>
                                  handleChange({
                                    target: {
                                      name: (
                                        mask as {
                                          el: { input: HTMLInputElement };
                                        }
                                      ).el.input.name,
                                      value: val,
                                    },
                                  } as unknown as React.ChangeEvent<HTMLInputElement>)
                                }
                                onBlur={handleBlur}
                                isInvalid={touched.phone && !!errors.phone}
                                isValid={touched.phone && !errors.phone}
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.phone}
                              </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group
                              as={Col}
                              sm={2}
                              controlId="formBasicPhoneExt"
                            >
                              <Form.Control
                                name="phoneExt"
                                placeholder="Ext"
                                value={values.phoneExt}
                                onChange={handleChange}
                                isInvalid={
                                  touched.phoneExt && !!errors.phoneExt
                                }
                                onBlur={handleBlur}
                                isValid={touched.phoneExt && !errors.phoneExt}
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.phoneExt}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Row>
                        </article>
                      </section>
                    </div>
                  </div>
                </div>
                <div className="employer-enroll-sidebar">
                  {/* @ts-expect-error _getPlanCode returns string | null; PlanCard treats absent as undefined */}
                  <PlanCard planCode={(() => this._getPlanCode(values))()} />
                  <AccountProcedureDisclosure />
                </div>
                <div className="employer-enroll-submit">
                  <div className="submit-row">
                    {this.props.error && (
                      <Alert type="error" msg={this.props.error} />
                    )}
                    <Button
                      name="submit"
                      withArrow={true}
                      loading={
                        this.props.isFetching || this.state.fetchingAddress
                      }
                    />
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    );
  };

  render() {
    let content;

    if (this.state.loading) {
      content = <IconSpinner centered />;
    } else if (this.state.showOwnerInfoPage) {
      content = <EmployerOwnerInfo />;
    } else {
      content = this.renderEmployerForm();
    }

    const progPercent = this.props.isMultiAccount ? "25" : "75";
    const savedPlanCode = this.props.savedPlanCode;
    const initialPlanCode = this.props.initialPlanCode;

    const showBillingRoute = this.state.showOwnerInfoPage
      ? (!!savedPlanCode && savedPlanCode !== ENTERPRISE) ||
        this.props.showsOrderBillingStep
      : !!initialPlanCode;
    return (
      <div className="employer-enroll">
        <ProgressBar
          isEmployer={true}
          activeStepId={COMPANY_INFO.id}
          progressPercent={progPercent}
          isMultiAccount={this.props.isMultiAccount}
          showBillingRoute={showBillingRoute}
        />
        <div>{content}</div>
      </div>
    );
  }
}

export default connector(withApollo<Props>(EmployerEnroll));
